import React, { useState, useEffect, useRef } from "react";
import { AlertCircle, Button, Select, TextInput, Tooltip, XClose } from "@opendate-app/setlist";
import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import SegmentPicker from "../../../../shared/SegmentPicker";

const filterRowContainerStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
  marginBottom: 8,
  alignItems: "flex-start",
};

const filterColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  flex: 1,
};

const filterInputStyle = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  maxWidth: 420,
};

const valueRowStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  width: "100%",
};

const eventItemPickerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  flex: 1,
  maxWidth: 420,
};

const formInputContainerStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const errorIconStyle = {
  display: "inline-flex",
  color: "var(--sl-text-error)",
};

const toOptions = (values) => values.map((value) => ({ value, label: value }));

const errorText = (errors) => [].concat(errors).join(" ");

const withLabels = (options) => options.map((option) => ({ ...option, label: option.label ?? "" }));

const valuelessConditions = [
  "exists",
  "does not exist",
  "is public",
  "is private",
  "includes current event",
  "does not include current event",
  "are sold out",
  "are not sold out",
];

const singleSelectConditions = [
  "is at venue",
  "is not at venue",
  "is in state",
  "is not in state",
]

const WorkflowNodeFilterForm = ({
  filter,
  setFilterFilterParams,
  index,
  deleteFilter,
}) => {
  const { team } = useCustomCampaignShowContext();

  const mountedRef = useRef(false);

  const [valueInputType, setValueInputType] = useState("none");
  const [filterFormState, setFilterFormState] = useState(filter);

  useEffect(() => {
    setFilterFormState(filter);
  }, [filter]);

  const supportedAssociationFilterTypes = filterFormState.filter_param_options?.association_filter_type || [];
  const supportedAttributes = filterFormState.filter_param_options?.attribute || [];
  const supportedConditions = filterFormState.filter_param_options?.condition || [];
  const selectOptions = withLabels(filterFormState.filter_param_options?.select_options || []);
  const eventOptions = withLabels(filterFormState.filter_param_options?.event_options || []);
  const itemOptions = withLabels(filterFormState.filter_param_options?.item_options || []);
  const itemOptionsEventId = filterFormState.filter_param_options?.item_options_event_id;
  const itemLabel = filterFormState.filter_param_options?.item_label || "item";

  const associationFilterType = filterFormState.filter_params?.association_filter_type || "";
  const attribute = filterFormState.filter_params?.attribute || "";
  const condition = filterFormState.filter_params?.condition || "";
  const values = filterFormState.filter_params?.values || [""];

  const [valueOne, setValueOne] = useState(values[0]);

  useEffect(() => {
    if (!filterFormState.filter_params?.condition) {
      setValueInputType("none");
    } else if (valuelessConditions.includes(filterFormState.filter_params?.condition)) {
      setValueInputType("none");
    } else if (filterFormState.filter_param_options?.value_input_type === "segment_picker") {
      setValueInputType("segment_picker");
    } else if (filterFormState.filter_param_options?.value_input_type === "event_item_picker") {
      setValueInputType("event_item_picker");
    } else if (
      singleSelectConditions.includes(filterFormState.filter_params?.condition) ||
      filterFormState.filter_param_options?.select_options
    ) {
      setValueInputType("single_select");
    } else {
      setValueInputType("text");
    }
  }, [
    filterFormState.filter_params?.condition,
    filterFormState.filter_param_options?.select_options,
    filterFormState.filter_param_options?.value_input_type,
  ]);

  useEffect(() => {
    if (valueOne === undefined) return;

    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (
      singleSelectConditions.includes(filterFormState.filter_params?.condition) ||
      valueInputType === "single_select" ||
      valueInputType === "segment_picker"
    ) {
      updateFilterParamsValueOne(valueOne);
    } else {
      const debounceUpdateValueOne = _.debounce(() => {
        updateFilterParamsValueOne(valueOne);
      }, 500);

      debounceUpdateValueOne();

      return () => {
        debounceUpdateValueOne.cancel();
      };
    }
  }, [valueOne]);

  const updateFilterParams = (payload) => {
    setFilterFormState({
      ...filter,
      filter_params: payload
    });
    setFilterFilterParams(index, payload);
  };

  const updateFilterParamsValueOne = (value) => {
    let newValues = filterFormState.filter_params.values || [];
    newValues[0] = value;

    updateFilterParams({
      ...filterFormState.filter_params,
      values: newValues
    });
  };

  const selectedValueOption = selectOptions.find(
    (option) => String(option.value) === String(valueOne)
  ) || null;

  const selectedEventId = values[0] || "";
  const selectedItemId = values[1] || "";

  const selectedEventOption = eventOptions.find(
    (option) => String(option.value) === String(selectedEventId)
  ) || null;

  const itemOptionsMatchSelectedEvent = selectedEventId !== "" &&
    String(itemOptionsEventId) === String(selectedEventId);

  const selectedItemOption = itemOptionsMatchSelectedEvent ? (itemOptions.find(
    (option) => String(option.value) === String(selectedItemId)
  ) || null) : null;

  const updateEventItemValues = (eventId, itemId) => {
    updateFilterParams({
      ...filterFormState.filter_params,
      values: [eventId, itemId],
    });
  };

  const renderErrorIcon = (param) => {
    return (
      filterFormState?.errors?.[param] ? (
        <Tooltip title={errorText(filterFormState.errors[param])}>
          <span style={errorIconStyle} tabIndex={0} role="img" aria-label={errorText(filterFormState.errors[param])}>
            <AlertCircle width={16} height={16} aria-hidden="true" />
          </span>
        </Tooltip>
      ) : null
    );
  };

  return (
    <div style={filterRowContainerStyle}>
      <div style={filterColumnStyle}>
        <div style={filterInputStyle}>
          <div style={formInputContainerStyle}>
            <Select
              aria-label="Filter type"
              placeholder="Filter type"
              width={200}
              options={toOptions(supportedAssociationFilterTypes)}
              value={associationFilterType || null}
              onChange={(value) => value !== associationFilterType && updateFilterParams({
                ...filterFormState.filter_params,
                association_filter_type: value,
                attribute: "",
                condition: "",
                values: [""],
                value_label: undefined,
              })}
            />
            {renderErrorIcon("association_filter_type")}
          </div>
          <div style={formInputContainerStyle}>
            <Select
              aria-label="Attribute"
              placeholder="Attribute"
              width={200}
              searchable
              options={toOptions(supportedAttributes)}
              value={attribute || null}
              onChange={(value) => value !== attribute && updateFilterParams({
                ...filterFormState.filter_params,
                attribute: value,
                condition: "",
                values: [""],
                value_label: undefined,
              })}
            />
            {renderErrorIcon("attribute")}
          </div>
          {attribute && (
            <div style={formInputContainerStyle}>
              <Select
                aria-label="Condition"
                placeholder="Select a condition"
                width={200}
                options={toOptions(supportedConditions)}
                value={condition || null}
                onChange={(value) => {
                  if (value === condition) return;
                  updateFilterParams({
                    ...filterFormState.filter_params,
                    condition: value,
                    values: [""],
                    value_label: undefined,
                  });
                  setValueOne(undefined);
                }}
              />
              {renderErrorIcon("condition")}
            </div>
          )}
        </div>
        {valueInputType === "segment_picker" ? (
          <div style={valueRowStyle}>
            <div style={{ flex: 1, maxWidth: 420 }}>
              <SegmentPicker
                team={team}
                value={{ segmentId: valueOne }}
                selectedLabel={filterFormState.filter_param_options?.value_label || filterFormState.filter_params?.value_label}
                onChange={(next) => {
                  setValueOne(next.segmentId);
                  updateFilterParams({
                    ...filterFormState.filter_params,
                    value_label: next.label,
                  });
                }}
                placeholder="Select a segment"
              />
            </div>
          </div>
        ) : valueInputType === "event_item_picker" ? (
          <div style={valueRowStyle}>
            <div style={eventItemPickerStyle} data-testid="event-item-picker">
              <div style={valueRowStyle}>
                <div style={{ flex: 1 }}>
                  <Select
                    aria-label="Event"
                    placeholder="Select an event"
                    searchable
                    options={eventOptions}
                    value={selectedEventOption ? selectedEventOption.value : null}
                    onChange={(value) => updateEventItemValues(value ?? "", "")}
                  />
                </div>
                {renderErrorIcon("value_one")}
              </div>
              {itemOptionsMatchSelectedEvent && (
                <div style={valueRowStyle}>
                  <div style={{ flex: 1 }}>
                    <Select
                      aria-label={itemLabel}
                      placeholder={`Select a ${itemLabel}`}
                      searchable
                      options={itemOptions}
                      value={selectedItemOption ? selectedItemOption.value : null}
                      onChange={(value) => updateEventItemValues(selectedEventId, value ?? "")}
                    />
                  </div>
                  {renderErrorIcon("value_two")}
                </div>
              )}
            </div>
          </div>
        ) : valueInputType === "single_select" ? (
          <div style={valueRowStyle}>
            <div style={{ flex: 1, maxWidth: 420 }}>
              <Select
                aria-label="Value"
                placeholder="Select a value"
                searchable
                options={selectOptions}
                value={selectedValueOption ? selectedValueOption.value : null}
                onChange={(value) => setValueOne(value ?? "")}
              />
            </div>
          </div>
        ) : valueInputType === "text" ? (
          <div style={valueRowStyle}>
            <div style={{ width: 200 }}>
              <TextInput
                aria-label="Value"
                disabled={!condition}
                value={valueOne || ""}
                onChange={(e) => setValueOne(e.target.value)}
                placeholder="value"
                error={filterFormState?.errors?.value_one ? errorText(filterFormState.errors.value_one) : undefined}
              />
            </div>
          </div>
        ) : null}
      </div>
      <Button variant="tertiary" size="sm" icon={XClose} aria-label="Remove condition" onClick={() => deleteFilter(index)} />
    </div>
  );
};

export default WorkflowNodeFilterForm;
